import { FileText } from 'lucide-react'
import { useState } from 'react'
import { AuditDetailRow } from '@/components/audit/audit-detail-row'
import { AuditFilters } from '@/components/audit/audit-filters'
import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '@/components/ui/status-badge'
import { useAuditLog } from '@/hooks/use-audit'
import type { FullAuditEntry } from '@/lib/types'

const PER_PAGE = 25
const MAX_DETAILS_LENGTH = 50

export function AuditLogPage(): React.JSX.Element {
  const [page, setPage] = useState(1)
  const [adminUser, setAdminUser] = useState('')
  const [action, setAction] = useState('')
  const [targetType, setTargetType] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const auditQuery = useAuditLog({
    page,
    per_page: PER_PAGE,
    admin_user: adminUser || undefined,
    action: action || undefined,
    target_type: targetType || undefined,
    from: dateFrom || undefined,
    to: dateTo || undefined,
  })

  const data = auditQuery.data?.data ?? []
  const total = auditQuery.data?.total ?? 0

  function handleFilterChange(): void {
    setPage(1)
  }

  function truncateDetails(details: Record<string, unknown> | null): string {
    if (!details) return '--'
    const str = JSON.stringify(details)
    return str.length > MAX_DETAILS_LENGTH ? `${str.slice(0, MAX_DETAILS_LENGTH)}...` : str
  }

  const columns = [
    {
      key: 'created_at',
      label: 'Timestamp',
      sortable: true,
      render: (row: FullAuditEntry) => (
        <span className="text-text-secondary">{new Date(row.created_at).toLocaleString()}</span>
      ),
    },
    {
      key: 'admin_email',
      label: 'Admin',
      render: (row: FullAuditEntry) => (
        <span className="font-medium text-text-primary">{row.admin_email}</span>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (row: FullAuditEntry) => (
        <StatusBadge status={row.action.split('.')[0] ?? row.action}>{row.action}</StatusBadge>
      ),
    },
    {
      key: 'target',
      label: 'Target',
      render: (row: FullAuditEntry) =>
        row.target_type ? (
          <span className="text-text-secondary">
            {row.target_type}
            {row.target_id ? `: ${row.target_id.slice(0, 8)}...` : ''}
          </span>
        ) : (
          <span className="text-text-tertiary">--</span>
        ),
    },
    {
      key: 'details',
      label: 'Details',
      render: (row: FullAuditEntry) => (
        <span className="max-w-xs truncate font-mono text-xs text-text-secondary">
          {truncateDetails(row.details)}
        </span>
      ),
    },
    {
      key: 'ip_address',
      label: 'IP',
      render: (row: FullAuditEntry) => (
        <span className="font-mono text-xs text-text-tertiary">{row.ip_address ?? '--'}</span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <FileText className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">Audit Log</h1>
      </div>

      <AuditFilters
        adminUser={adminUser}
        action={action}
        targetType={targetType}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onAdminUserChange={(v) => {
          setAdminUser(v)
          handleFilterChange()
        }}
        onActionChange={(v) => {
          setAction(v)
          handleFilterChange()
        }}
        onTargetTypeChange={(v) => {
          setTargetType(v)
          handleFilterChange()
        }}
        onDateFromChange={(v) => {
          setDateFrom(v)
          handleFilterChange()
        }}
        onDateToChange={(v) => {
          setDateTo(v)
          handleFilterChange()
        }}
      />

      <DataTable<FullAuditEntry>
        columns={columns}
        data={data}
        isLoading={auditQuery.isLoading}
        emptyMessage="No admin actions recorded yet"
        pagination={{ page, perPage: PER_PAGE, total }}
        onPageChange={setPage}
        onRowClick={(row) => setExpandedId((prev) => (prev === row.id ? null : row.id))}
      />

      {expandedId && data.find((e) => e.id === expandedId) && (
        <AuditDetailRow entry={data.find((e) => e.id === expandedId) as FullAuditEntry} />
      )}
    </div>
  )
}
